import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { COMPLIANCE_MAP, DEVICE_ICON, DEVICE_COLOR } from '../data/mockData';
import { Ic, ICONS } from '../data/icons';
import { Badge, StatusDot, MiniBar, Avatar, Card, CardHeader, Modal, toast, TableSkeleton } from '../components/shared';
import { fetchDeviceDetail, sendCommand } from '../api/client';
import DraggableResizableModal from '../components/shared/DraggableResizableModal';

function RemoteDesktopModalContent({ remoteSessionInfo }) {
  // Eğer ileride hook kullanmak gerekirse burada koşulsuz çağrılır
  if (remoteSessionInfo?.status === 'pending') {
    return (
      <div style={{textAlign:'center',marginTop:40}}>
        <div style={{fontSize:18,marginBottom:16}}>Bağlantı başlatılıyor...</div>
        <div style={{color:'#64748b'}}>Cihazdan bağlantı yanıtı bekleniyor.</div>
      </div>
    );
  }
  if (remoteSessionInfo?.url) {
    return (
      <iframe src={remoteSessionInfo.url} title="Uzak Masaüstü" style={{width:'100%',height:'320px',border:'none',borderRadius:8}} allowFullScreen />
    );
  }
  return null;
}

export default function DeviceDetail({ C }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [commands, setCommands] = useState([]);
  const [showWipeConfirm, setShowWipeConfirm] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState('');
  const [showRemoteModal, setShowRemoteModal] = useState(false);
  const [remoteSessionInfo, setRemoteSessionInfo] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    fetchDeviceDetail(id)
      .then(data => {
        if (data) {
          setDevice(data);
        } else {
          setError('Cihaz bulunamadı');
          toast.error('Cihaz bulunamadı');
        }
      })
      .catch(err => {
        const msg = err.message || 'Cihaz yüklenemedi';
        setError(msg);
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const pushCommand = async (type, extra = {}) => {
    setSending(type);
    try {
      const result = await sendCommand(id, type, extra);
      const labelMap = { lock:'Cihaz kilitle', wipe:'Cihazı sıfırla', restart:'Yeniden başlat', message:'Mesaj gönder', remote:'Uzak Masaüstü' };
      const colorMap = { lock:C.amber, wipe:C.red, restart:C.accent, message:C.green, remote:C.blue||'#3b82f6' };
      setCommands(prev => [{
        id: result.id || `cmd_${Date.now()}`,
        type, label:labelMap[type]||type,
        time: new Date().toLocaleTimeString('tr-TR',{hour:'2-digit',minute:'2-digit'}),
        status:'Kuyruğa alındı', extra, color:colorMap[type]||C.accent,
      }, ...prev]);
      if(type==='remote') {
        setShowRemoteModal(true);
        setRemoteSessionInfo({ status:'pending', ...extra });
      }
      toast.success(`${labelMap[type]} komutu gönderildi`);
    } catch (err) {
      toast.error(err?.message || 'Komut gönderilemedi');
    }
    finally { setSending(''); }
  };

  // ✅ LOADING STATE
  if (loading) return (
    <Card C={C}><div style={{ padding:16 }}><TableSkeleton rows={5} cols={4} C={C} /></div></Card>
  );

  // ✅ ERROR STATE - DEVICE GÜVENLİ KONTROLÜ
  if (error || !device) return (
    <Card C={C}>
      <div style={{ padding:48, textAlign:'center' }}>
        <div style={{ fontSize:16, fontWeight:700, color:C.text, marginBottom:12 }}>{error || 'Cihaz bulunamadı'}</div>
        <button onClick={() => navigate('/devices')} style={{ padding:'8px 18px', borderRadius:9, border:`1px solid ${C.border}`, background:'transparent', color:C.textSub, cursor:'pointer', fontSize:13, fontWeight:600 }}>← Cihaz listesine dön</button>
      </div>
    </Card>
  );

  // ✅ BU NOKTADA DEVICE 100% GARANTILI VARA
  const [cc, cl] = COMPLIANCE_MAP[device.compliance] || ['#94a3b8', device.compliance];
  const sc = device.storage > 80 ? C.red : device.storage > 60 ? C.amber : C.green;
  
  // ✅ GÜVENLI USER INITIALS HESAPLAMA
  const userInitials = (device.user && device.user.trim()) 
    ? device.user.split('.').map((s) => s[0].toUpperCase()).join('')
    : '??';

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <Modal isOpen={showWipeConfirm} onClose={() => setShowWipeConfirm(false)} onConfirm={() => pushCommand('wipe')}
        title='Cihazı Sıfırlamayı Onayla'
        message={`"${device.name}" cihazına uzaktan sıfırlama komutu gönderilecek. Tüm veriler silinir ve bu işlem geri alınamaz.`}
        type='danger' C={C} confirmLabel='Evet, Sıfırla' />

      <button onClick={() => navigate('/devices')} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12.5, color:C.textSub, background:'none', border:'none', padding:0, cursor:'pointer', marginBottom:4 }}>
        <Ic d={ICONS.chevL} size={13} s={C.textSub} /> Cihaz listesine dön
      </button>

      <div style={{ display:'grid', gridTemplateColumns:'minmax(0,2fr) minmax(0,1.4fr)', gap:16 }}>
        {/* Left column */}
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {/* Main info */}
          <Card C={C}>
            <div style={{ padding:'18px 20px', display:'flex', alignItems:'flex-start', gap:14 }}>
              <div style={{ width:52, height:52, borderRadius:14, background:`${DEVICE_COLOR[device.type]}20`, color:DEVICE_COLOR[device.type], display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Ic d={ICONS[DEVICE_ICON[device.type]]} size={26} />
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, marginBottom:8 }}>
                  <div>
                    <div style={{ fontSize:20, fontWeight:800, color:C.text, letterSpacing:'-0.5px' }}>{device.name}</div>
                    <div style={{ fontSize:12, color:C.textFaint, marginTop:2 }}>{device.os} · Seri: {device.serial}</div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12.5, color:C.textSub }}>
                      <StatusDot online={device.status==='online'} />
                      {device.status==='online'?'Çevrimiçi':'Çevrimdışı'}
                    </div>
                    <div style={{ fontSize:10.5, color:C.textFaint, marginTop:2 }}>{device.lastSeen} önce</div>
                  </div>
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  <Badge color={device.encrypted?C.green:C.red}>{device.encrypted?'🔒 Şifreli':'⚠️ Şifreleme kapalı'}</Badge>
                  <Badge color={cc}>{cl}</Badge>
                  <Badge color={DEVICE_COLOR[device.type]}>{device.type.toUpperCase()}</Badge>
                </div>
              </div>
            </div>
            <div style={{ padding:'14px 20px', borderTop:`1px solid ${C.border}`, display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:14 }}>
              <div>
                <div style={{ fontSize:11, color:C.textFaint, textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:6 }}>Kullanıcı</div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <Avatar initials={userInitials} size={28} />
                  <div>
                    <div style={{ fontSize:13, color:C.text, fontWeight:600 }}>{device.user || 'Bilinmeyen'}</div>
                    <div style={{ fontSize:11, color:C.textFaint }}>{device.dept || '-'}</div>
                  </div>
                </div>
              </div>
              <div>
                <div style={{ fontSize:11, color:C.textFaint, textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:6 }}>Depolama</div>
                <div style={{ fontSize:12, color:C.textSub, marginBottom:5 }}>{device.storage}% kullanılıyor</div>
                <MiniBar pct={device.storage} color={sc} C={C} />
              </div>
              <div>
                <div style={{ fontSize:11, color:C.textFaint, textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:6 }}>Pil</div>
                <div style={{ fontSize:12, color:C.textSub, marginBottom:5 }}>{device.battery===0?'Kapalı/Şarjda':`%${device.battery}`}</div>
                <MiniBar pct={device.battery} color={device.battery>60?C.green:device.battery>30?C.amber:C.red} C={C} />
              </div>
            </div>
          </Card>

          {/* Info cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:12 }}>
            {[['Uygulamalar','FAZ 4\'te eklenecek'],['Ağ & Konum','FAZ 6 API entegrasyonu'],['Politika Uyumluluğu','FAZ 3\'te detaylanacak']].map(([t,d]) => (
              <Card key={t} C={C}>
                <CardHeader C={C} title={t} />
                <div style={{ padding:'14px 16px', fontSize:12, color:C.textFaint }}>{d}</div>
              </Card>
            ))}
          </div>
        </div>

        {/* Right column — commands */}
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <Card C={C}>
            <CardHeader C={C} title='Uzaktan Komutlar' />
            <div style={{ padding:14, display:'flex', flexDirection:'column', gap:10 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {[
                  { type:'lock',    label:'🔒 Kilitle',       style:{ border:`1px solid ${C.border}`, background:C.surface2, color:C.text } },
                  { type:'restart', label:'🔄 Yeniden Başlat', style:{ border:`1px solid ${C.border}`, background:C.surface2, color:C.text } },
                  { type:'wipe',    label:'🗑 Sıfırla (Wipe)', style:{ border:`1px solid ${C.red}66`, background:C.redBg, color:C.red }, danger:true },
                  { type:'message', label:'💬 Mesaj Gönder',   style:{ border:`1px solid ${C.accentBorder}`, background:C.accentBg, color:C.accent } },
                  { type:'remote',  label:'🖥 Uzak Masaüstü',  style:{ border:`1px solid ${C.blue||'#3b82f6'}`, background:C.blueBg||'#e0e7ff', color:C.blue||'#3b82f6' } },
                ].map(btn => (
                  <button key={btn.type}
                    onClick={() => btn.danger ? setShowWipeConfirm(true) : btn.type==='message' ? (message.trim() ? pushCommand('message',{message:message.trim()}) : toast.error('Önce mesaj yazın')) : btn.type==='remote' ? pushCommand('remote') : pushCommand(btn.type)}
                    disabled={sending===btn.type}
                    style={{ padding:'9px 10px', borderRadius:9, cursor:'pointer', fontSize:12, fontWeight:600, display:'flex', alignItems:'center', justifyContent:'center', gap:6, opacity:sending===btn.type?0.6:1, ...btn.style }}>
                    {sending===btn.type ? 'Gönderiliyor...' : btn.label}
                  </button>
                ))}
              </div>
              <div>
                <div style={{ fontSize:11, color:C.textFaint, marginBottom:6 }}>Cihaza mesaj gönder</div>
                <textarea rows={3} value={message} onChange={e=>setMessage(e.target.value)} placeholder='Ekranda gösterilecek kısa bir mesaj yazın...'
                  style={{ width:'100%', resize:'vertical', borderRadius:10, border:`1px solid ${C.border}`, background:C.surface2, color:C.text, fontSize:12.5, padding:'9px 11px', fontFamily:'inherit', outline:'none' }} />
              </div>
            </div>
          </Card>

          <Card C={C}>
            <CardHeader C={C} title='Komut Geçmişi' action={commands.length > 0 && <button onClick={() => setCommands([])} style={{ fontSize:11, color:C.textFaint, background:'none', border:'none', cursor:'pointer' }}>Temizle</button>} />
            {commands.length === 0 ? (
              <div style={{ padding:'24px', fontSize:12, color:C.textFaint, textAlign:'center' }}>Henüz komut gönderilmedi.</div>
            ) : commands.map(cmd => (
              <div key={cmd.id} style={{ padding:'10px 16px', borderTop:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:cmd.color||C.accent, flexShrink:0 }} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12.5, color:C.text, fontWeight:500 }}>{cmd.label}</div>
                  {cmd.extra?.message && <div style={{ fontSize:11, color:C.textFaint, marginTop:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>"{cmd.extra.message}"</div>}
                  <div style={{ fontSize:10.5, color:C.textFaint, marginTop:1 }}>{cmd.time} · {cmd.status}</div>
                </div>
              </div>
            ))}
          </Card>
        </div>
      </div>

      {/* Uzak Masaüstü Modal */}
      <DraggableResizableModal
        isOpen={showRemoteModal}
        onClose={() => setShowRemoteModal(false)}
        title={`Uzak Masaüstü Oturumu`}
        initialWidth={700}
        initialHeight={420}
      >
        <RemoteDesktopModalContent remoteSessionInfo={remoteSessionInfo} />
      </DraggableResizableModal>
    </div>
  );
}
